import { format } from 'date-fns';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'react-use';

import { CalendarRangeIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DateInput, type DateInputProps } from '@air/primitive-date-input';
import { parseAirDateToISO } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

export type EditableDateProps = {
  'data-testid'?: string;
  isEditing?: boolean;
  /**
   * This will set the max character length for the textarea.
   */
  onEditingStateChange?: (isEditingState: boolean) => void;
  readOnly?: boolean;
  dateValue?: string;
  onChange: (value: Date | null) => void;
  showCalendarIcon?: boolean;
  buttonClassName?: string;
} & Pick<DateInputProps, 'placeholder' | 'id' | 'size' | 'name'>;

export const EditableDate = memo(
  ({
    // biome-ignore lint/complexity/useLiteralKeys: PLA-1724 — pre-existing debt, not introduced by the icon migration
    ['data-testid']: testId,
    isEditing = false,
    onEditingStateChange,
    placeholder,
    readOnly,
    name,
    dateValue = '',
    onChange,
    size = 'large',
    buttonClassName,
    showCalendarIcon = true,
  }: EditableDateProps) => {
    const [isEditingState, setIsEditingState] = useState(isEditing);
    const isPreviousIsEditing = usePrevious(isEditingState);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dateInputRef = useRef<HTMLInputElement>(null);

    const setIsEditing = useCallback(
      (isEditing: boolean) => {
        setIsEditingState(isEditing);
        onEditingStateChange?.(isEditing);
      },
      [onEditingStateChange],
    );

    const buttonText = useMemo(() => {
      /** `dateValue` shows Invalid Date on Safari, so we reformat it here */
      const formattedDate = dateValue ? parseAirDateToISO(dateValue).toString() : undefined;
      try {
        return formattedDate ? format(new Date(formattedDate), 'MM/dd/yyyy') : placeholder;
      } catch (error) {
        reportErrorToBugsnag({
          context: 'Failed to format date in EditableDate',
          error,
          metadata: {
            dateValue,
            formattedDate,
          },
        });

        return '';
      }
    }, [dateValue, placeholder]);

    useEffect(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
      if (isEditingState && dateInputRef?.current) {
        dateInputRef.current?.focus();
        dateInputRef.current.setSelectionRange(dateInputRef.current.value.length, dateInputRef.current.value.length);
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
      if (!isEditingState && isPreviousIsEditing && buttonRef?.current) {
        buttonRef.current.focus();
      }
    }, [isEditingState, isPreviousIsEditing]);

    return (
      <div className={cn('flex w-full rounded-4', readOnly ? 'cursor-default' : 'cursor-pointer')} data-testid={testId}>
        {isEditingState ? (
          <DateInput
            autoFocus
            className="w-full"
            name={name}
            // biome-ignore lint/style/noMagicNumbers lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onBlur={() => setTimeout(() => setIsEditing(false), 100)}
            // biome-ignore lint/style/noMagicNumbers lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onCalendarClose={() => setTimeout(() => setIsEditing(false), 100)}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onChange={(date: Date | [Date, Date] | null) => {
              // biome-ignore lint/style/useBlockStatements: PLA-1724 — pre-existing debt, not introduced by the icon migration
              if (Array.isArray(date)) return;
              setIsEditing(false);
              onChange(date);
            }}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onSelect={(_, e) => {
              e?.stopPropagation();
            }}
            placeholderText={placeholder}
            ref={dateInputRef}
            // timeouts are so clear button has a chance to trigger before close/blur do which unrender the component
            selected={dateValue ? parseAirDateToISO(dateValue) : undefined}
            showCalendarIcon={showCalendarIcon}
            size={size}
            testId="DATE_INPUT_FIELD"
          />
        ) : (
          <Button
            appearance="ghost"
            className={cn(
              'w-full justify-start pl-2 font-normal disabled:bg-transparent',
              !dateValue ? 'text-gray-7' : '',
              buttonClassName,
            )}
            color="gray"
            disabled={readOnly}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => {
              setIsEditing(true);
            }}
            prefix={showCalendarIcon ? <CalendarRangeIcon size="sm" /> : undefined}
            ref={buttonRef}
            size="large"
          >
            {buttonText}
          </Button>
        )}
      </div>
    );
  },
);

EditableDate.displayName = 'EditableDate';
